
import { useState, useMemo } from "react";
import { usePagination } from "@/hooks/usePagination";
import { Tables } from "@/integrations/supabase/types";

type ActivityCategory = Tables<"activity_categories">;

interface UseActivityCategoriesManagerProps {
  categories: ActivityCategory[];
  searchTerm: string;
  filters: {
    status: string;
  };
  saveCategory: (categoryData: any, editingCategoryId?: string) => Promise<boolean>;
}

export const useActivityCategoriesManager = ({ 
  categories, 
  searchTerm, 
  filters, 
  saveCategory 
}: UseActivityCategoriesManagerProps) => {
  const [editingCategory, setEditingCategory] = useState<ActivityCategory | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Filter and search categories
  const filteredCategories = useMemo(() => {
    return categories.filter((category) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          category.name.toLowerCase().includes(searchLower) ||
          category.description?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && category.status !== filters.status) {
        return false;
      }

      return true;
    });
  }, [categories, searchTerm, filters]);

  // Pagination
  const pagination = usePagination({
    items: filteredCategories,
    itemsPerPage: 10,
  });

  const handleSave = async (categoryData: any) => {
    const success = await saveCategory(categoryData, editingCategory?.id);
    if (success) {
      setEditingCategory(null);
      setIsCreating(false);
    }
  };

  const startCreating = () => setIsCreating(true);
  const startEditing = (category: ActivityCategory) => setEditingCategory(category);
  const cancelEditing = () => {
    setEditingCategory(null);
    setIsCreating(false);
  };

  return {
    editingCategory,
    isCreating,
    filteredCategories,
    pagination,
    handleSave,
    startCreating,
    startEditing,
    cancelEditing,
  };
};
